import { useState, useEffect } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { api } from './api';

export default function ComposeModal({ lead, replyTo, onClose, onSent }) {
  const [subject, setSubject] = useState(replyTo ? `Re: ${replyTo.subject || ''}`.replace(/^Re:\s*Re:/i, 'Re:') : '');
  const [html, setHtml] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [signaturLoaded, setSignaturLoaded] = useState(false);

  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: false }), Placeholder.configure({ placeholder: 'Nachricht …' })],
    content: '',
    onUpdate: ({ editor: ed }) => setHtml(ed.getHTML()),
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none min-h-[220px] max-h-[400px] overflow-y-auto px-3 py-2 text-[13px] focus:outline-none text-[var(--text-primary)]',
      },
    },
  });

  useEffect(() => {
    if (signaturLoaded || !editor) return;
    api('/api/sales/signatur').then((data) => {
      if (data.signatur) {
        const init = `<p><br></p><p>--</p>${data.signatur}`;
        editor.commands.setContent(init);
        setHtml(init);
      }
      setSignaturLoaded(true);
    }).catch(() => setSignaturLoaded(true));
  }, [editor, signaturLoaded]);

  const send = async () => {
    if (!subject.trim() || !html.trim()) return;
    setSending(true);
    setError('');
    try {
      const body = {
        betreff: subject,
        inhalt_html: html,
        ...(replyTo?.message_id ? { in_reply_to: replyTo.message_id } : {}),
      };
      await api(`/api/leads/${lead.id}/email-senden`, { method: 'POST', body });
      onSent?.();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-[60]" onClick={onClose}>
      <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-2xl mx-0 sm:mx-4 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-card)] flex-shrink-0">
          <div className="flex flex-col">
            <span className="text-[14px] font-semibold text-[var(--text-primary)]">{replyTo ? 'Antworten' : 'Neue E-Mail'}</span>
            <span className="text-[11px] text-[var(--text-tertiary)]">an {lead.email || '—'}</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)]"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Betreff"
            className="w-full px-3 py-2 rounded-lg border border-[var(--border-card)] bg-[var(--bg-card)] text-[13px] focus:outline-none focus:border-[#338edc] text-[var(--text-primary)]"
          />
          <div className="border border-[var(--border-card)] rounded-lg bg-[var(--bg-card)]">
            <EditorContent editor={editor} />
          </div>
          {error && <div className="text-[12px] text-red-600 px-3 py-2 bg-red-50 rounded">{error}</div>}
        </div>

        <div className="flex items-center gap-2 px-4 py-3 border-t border-[var(--border-card)] flex-shrink-0">
          <button onClick={onClose} className="px-3 py-2 text-[12px] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] rounded-lg">Abbrechen</button>
          <button
            onClick={send}
            disabled={sending || !subject.trim() || !html.trim()}
            className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#338edc] text-white text-[12px] font-medium hover:opacity-90 disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {sending ? 'Sende …' : 'Senden'}
          </button>
        </div>
      </div>
    </div>
  );
}
